<template>
  <div class="xhs-activity-table" v-loading="loading">
    <el-table :data="tableData" border size="small" style="width: 100%">
      <!-- 笔记序号：从 1 起，历史「未通过」记录同样占号（后端 listByOrder 已按序编号） -->
      <el-table-column prop="seq" label="笔记序号" width="85" align="center"></el-table-column>
      <el-table-column prop="submitTime" label="笔记提交时间" min-width="160" align="center"></el-table-column>

      <el-table-column label="小红书推文链接" min-width="320" align="center">
        <template slot-scope="{ row }">
          <span v-if="!row.noteUrl">-</span>
          <span v-else>
            <span class="link-cell">
              <a class="link-text" :title="row.noteUrl" @click="openNoteUrl(row.noteUrl)">{{ row.noteUrl }}</a>
              <button class="mini-btn plain" @click="copyText(row.noteUrl)">复制</button>
            </span>
          </span>
        </template>
      </el-table-column>

      <el-table-column label="推文截图" min-width="180" align="center">
        <template slot-scope="{ row }">
          <div class="screenshot-cell">
            <img v-for="item in screenshotsOf(row)" :key="item.key" class="screenshot-thumb" :src="item.url"
              :title="item.label + '：点击查看大图'" @click="viewImage(item.url)" />
            <span v-if="!screenshotsOf(row).length">-</span>
          </div>
        </template>
      </el-table-column>

      <!-- 后台文案，不用后端的 statusDesc（那是 C 端口径） -->
      <el-table-column label="小红书活动状态" min-width="140" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="statusTagType[row.status] || 'info'" size="small">
            {{ statusMap[row.status] || row.status || "-" }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="rejectReason" label="不通过原因" min-width="160" align="center" show-overflow-tooltip></el-table-column>
      <el-table-column prop="auditUserName" label="审核人" min-width="110" align="center"></el-table-column>
      <el-table-column prop="auditTime" label="审核时间" min-width="160" align="center"></el-table-column>

      <el-table-column label="展示期起止" min-width="290" align="center">
        <template slot-scope="{ row }">
          <span v-if="!row.showStartTime && !row.showEndTime">-</span>
          <span v-else>{{ row.showStartTime || "-" }} ~ {{ row.showEndTime || "-" }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="showRemainingDays" label="剩余展示天数" width="115" align="center" :formatter="remainText"></el-table-column>
      <el-table-column prop="expectedMonths" label="预期奖励月数" width="115" align="center"></el-table-column>
      <el-table-column prop="grantMonths" label="实际奖励月数" width="115" align="center" :formatter="dash"></el-table-column>
      <el-table-column label="奖励发放状态" min-width="125" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="grantStatusTagType(row)" size="small">{{ row.grantStatusDesc || "未发放" }}</el-tag>
        </template>
      </el-table-column>
      <!-- 服务费支付流水号：点击切到本页「平台服务费信息」Tab（PRD §3.2.1 #18 要求可跳转服务费信息；
           本条已在订单详情内，故不再跳页，改为切换页签，由父级 detail.vue 处理） -->
      <el-table-column label="服务费支付流水号" min-width="190" align="center">
        <template slot-scope="{ row }">
          <a v-if="row.feeRecordId" class="link-text" :title="row.feeRecordId"
            @click="$emit('go-fee-tab', row.feeRecordId)">{{ row.feeRecordId }}</a>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="grantUserName" label="发放人" min-width="110" align="center" :formatter="dash"></el-table-column>
      <el-table-column prop="grantTime" label="发放时间" min-width="160" align="center" :formatter="dash"></el-table-column>

      <!-- 操作：横排（inline-flex）；按钮状态机由后端派生，前端只渲染 -->
      <el-table-column label="操作" min-width="190" align="center" fixed="right">
        <template slot-scope="{ row }">
          <template v-if="row.canAudit">
            <button class="mini-btn" @click="openAuditDialog(row, 'pass')">审核通过</button>
            <button class="mini-btn danger" @click="openAuditDialog(row, 'reject')">审核不通过</button>
          </template>
          <button v-else-if="row.canGrant" class="mini-btn warn" @click="openGrantDialog(row)">去发放奖励</button>
          <button v-else-if="row.canRetry" class="mini-btn" @click="handleRetry(row)">重试发放</button>
          <template v-else>
            <button class="mini-btn" disabled :title="actionTitle(row)">{{ actionLabel(row) }}</button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <!-- 审核 / 发放弹窗：与列表页共用同一组件 -->
    <XhsAuditDialog :visible.sync="auditVisible" :row="actionRow" :mode="auditMode" @success="fetchData" />
    <!-- 发放奖励：复用「配置服务费」弹窗（来源锁死活动赠送、月数进来就是预期奖励月数），
         确认仍打 698 发放接口——不另建一套发放弹窗 -->
    <FeeConfigDialog
      :visible.sync="grantVisible"
      :user-name="actionRow && actionRow.userName"
      :user-mobile="actionRow && actionRow.mobile"
      :mask-mobile="false"
      :activity-name="actionRow && actionRow.activityName"
      lock-gift-type="ACTIVITY_GIFT"
      :preset-months="actionRow && actionRow.expectedMonths"
      :unit-price="actionRow && actionRow.serviceUnitPrice"
      :submitting="grantSubmitting"
      @submit="submitGrant"
    />

    <el-dialog :visible.sync="imageDialogVisible" width="720px" append-to-body title="截图预览">
      <img v-if="imageUrl" :src="imageUrl" style="width: 100%" alt="截图" />
    </el-dialog>
  </div>
</template>

<script>
import pageConfig, { fileUrl, statusTagType, grantStatusTagType } from "./page-config";
import XhsAuditDialog from "./xhs-audit-dialog.vue";
import FeeConfigDialog from "../components/FeeConfigDialog.vue";

/**
 * 订单详情内的「小红书活动」Tab（ADM-2）。
 *
 * 与列表页同源：调 POST /xhsActivity/admin/listByOrder（入参 orderNo），返回该订单下全部笔记，
 * **不分页**（单订单记录量小），笔记序号由后端按「提交时间 + 状态序」从 1 起编号、历史未通过记录占号。
 *
 * 操作按钮的置灰/文案一律由后端派生（canAudit/canGrant/canRetry + operationDisabledReason），
 * 本组件不自行判断状态机——避免与列表页各判一套。
 */
export default {
  name: "XhsActivityTable",
  components: { XhsAuditDialog, FeeConfigDialog },
  props: {
    /** 活动订单号（父级 detail.vue 传入） */
    activityorderNo: { type: String, default: "" },
  },
  data() {
    return {
      statusMap: pageConfig.statusMap,
      statusTagType,
      loading: false,
      tableData: [],
      auditVisible: false,
      auditMode: 'pass',
      grantVisible: false,
      grantSubmitting: false,
      actionRow: null,
      imageDialogVisible: false,
      imageUrl: "",
    };
  },
  watch: {
    activityorderNo: {
      immediate: true,
      handler(val) {
        if (val) this.fetchData();
      },
    },
  },
  methods: {
    // 模板里要用到它：page-config 的**导出函数不会自动挂到组件实例上**，必须放进 methods，
    // 否则模板求值时是 undefined → is not a function → 表体渲染整块失败（页面表现就是「没数据」）。
    grantStatusTagType,

    fetchData() {
      const _ = this;
      if (!_.activityorderNo) {
        _.tableData = [];
        return;
      }
      _.loading = true;
      _.$H.post(_, _.$A.xhsActivity.listByOrder, { orderNo: _.activityorderNo })
        .then((res) => {
          const payload = (res && res.data && res.data.data) || (res && res.data) || [];
          _.tableData = Array.isArray(payload) ? payload : payload.content || [];
        })
        .catch(() => {
          _.tableData = [];
        })
        .finally(() => {
          _.loading = false;
        });
    },

    openAuditDialog(row, mode) {
      this.actionRow = row;
      this.auditMode = mode === 'reject' ? 'reject' : 'pass';
      this.auditVisible = true;
    },

    /** 操作列文案/提示：照原型——展示中未满 15 天显示「耐心等待发放」，剩余天数放 title */
    actionLabel(row) {
      if (row.status === 'showing' && row.showRemainingDays > 0) return '耐心等待发放';
      return pageConfig.statusMap[row.status] || '—';
    },
    actionTitle(row) {
      if (row.status === 'showing' && row.showRemainingDays > 0) {
        return '推文需保持展示 15 天，剩余 ' + row.showRemainingDays + ' 天可发放';
      }
      return row.operationDisabledReason || '';
    },

    openGrantDialog(row) {
      this.actionRow = row;
      this.grantVisible = true;
    },

    /**
     * 发放确认：弹窗里的「服务费配置周期」即实际奖励月数（默认已预填预期月数，运营可改）。
     * 赠送来源不提交——后端 /xhsActivity/admin/grant 固定用 ACTIVITY_GIFT，前端锁定只是 UI 约束。
     */
    submitGrant(payload) {
      const _ = this;
      const row = _.actionRow || {};
      _.grantSubmitting = true;
      _.$H.post(_, _.$A.xhsActivity.grant, {
        noteNo: row.noteNo,
        actualMonths: payload.giftMonths,
      })
        .then(() => {
          _.$U.success("发放完成");
          _.grantVisible = false;
          _.fetchData();
        })
        .finally(() => {
          _.grantSubmitting = false;
        });
    },

    /** 重试发放：仅后端判定为「发放失败 / 占位陈旧」的行才会出现该按钮 */
    handleRetry(row) {
      const _ = this;
      _.$confirm("确认重试发放？将按实际奖励月数重新出账。", "二次确认", {
        confirmButtonText: "确认",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          _.loading = true;
          return _.$H
            .post(_, _.$A.xhsActivity.grantRetry, {
              noteNo: row.noteNo,
              actualMonths: row.grantMonths || row.expectedMonths || undefined,
            })
            .then(() => {
              _.$U.success("重试成功");
              _.fetchData();
            })
            .finally(() => {
              _.loading = false;
            });
        })
        .catch(() => {
          // 用户取消二次确认
        });
    },

    screenshotsOf(row) {
      const list = [];
      if (row.screenshotAccount) list.push({ key: "account", label: "账号截图", url: fileUrl(row.screenshotAccount) });
      if (row.screenshotPublish) list.push({ key: "publish", label: "发布详情截图", url: fileUrl(row.screenshotPublish) });
      if (row.screenshotNote) list.push({ key: "note", label: "笔记截图", url: fileUrl(row.screenshotNote) });
      return list;
    },

    ellipsis(text, max) {
      if (!text) return "";
      return text.length > max ? text.slice(0, max) + "..." : text;
    },

    /** 剩余展示天数：>0 → “N 天”，否则「已满足」（与列表页/原型一致） */
    remainText(row, col, val) {
      if (val === null || val === undefined || val === "") return "-";
      return Number(val) > 0 ? val + " 天" : "已满足";
    },

    /** 空值统一显示 '-'（与列表页一致；空着看不出是没数据还是渲染漏了） */
    dash(row, col, val) {
      return val === null || val === undefined || val === "" ? "-" : val;
    },

    openNoteUrl(url) {
      if (url) window.open(url, "_blank");
    },

    /** 复制完整链接：优先 clipboard API，不可用时回退 execCommand */
    copyText(text) {
      if (!text) return;
      const done = () => this.$U.success("已复制链接");
      const fallback = () => {
        const input = document.createElement("input");
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        done();
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(fallback);
      } else {
        fallback();
      }
    },

    viewImage(url) {
      if (!url) return;
      this.imageUrl = url;
      this.imageDialogVisible = true;
    },
  },
};
</script>

<style scoped>
.link-text {
  color: #409eff;
  cursor: pointer;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}
.link-cell {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.link-cell .mini-btn {
  flex-shrink: 0;
}
.screenshot-cell {
  display: flex;
  gap: 4px;
  justify-content: center;
}
.screenshot-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
}
/* 操作列按钮：照后台原型 .mini-btn */
.mini-btn {
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #1677ff;
  background: #1677ff;
  color: #fff;
}
.mini-btn.danger {
  background: #fff;
  color: #f56c6c;
  border-color: #f56c6c;
}
.mini-btn.warn {
  background: #e6a23c;
  border-color: #e6a23c;
  color: #fff;
}
.mini-btn:disabled {
  background: #f5f7fa;
  color: #c0c4cc;
  border-color: #e5e8ef;
  cursor: not-allowed;
}
.mini-btn + .mini-btn {
  margin-left: 6px;
}
.operate-none {
  color: #909399;
}
</style>
